import { useLang } from '@/i18n';
import gtm from '@/utils/gtm';
import ShopifyCartIcon from '@elem/compCommon/ShopifyCartIcon';
import Naver, { EfProductNaverItemProps, EfProductNaverProps } from '@elem/ui/ef-product-naver';
import ShopifyShoppingCart from 'elem/compCommon/ShopifyShoppingCart';
import { ShopifyCartItemType } from 'elem/compCommon/ShopifyShoppingCart/const';
import { langText } from 'elem/compPage/Delta3Series/detail/comps/P/EFCardCartLists/langText';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { theme } from './config/page';
import css from './index.module.scss';
import { useProduct } from './ProductProvider';
import Bundle from './tabs/Bundle';
import Overview from './tabs/Overview';
import Spec from './tabs/Spec';
import Support from './tabs/Support';

const DPUX: React.FC = () => {
    const { state, dispatch } = useProduct();
    const { t } = useLang('d3u');
    const { locale = '' } = useRouter();
    const { productSupport, productSpecs, activeTab } = state;
    const [activetedTabs, setActivetedTabs] = useState<string[]>([activeTab]);

    const naverProps: EfProductNaverProps = {
        theme: theme as 'black' | 'white',
        title: t('naver_title'),
        tabs: [
            {
                key: 'Product',
                text: t('naver_tab_product'),
            },
            {
                key: 'Spec',
                text: t('naver_tab_spec'),
            },
            {
                key: 'Support',
                text: t('naver_tab_support'),
            },
        ],
        buyItem: {
            key: 'Bundles',
            text: t('naver_tab_buy'),
        },
        activeTab,
        buyNowGtm: {
            event: 'uaEvent',
            event_name: 'click_anchor',
            position: t('C_tab1'),
            button_name: t('naver_tab_bundle'),
        },
        callbackBuy: () => {
            if (activeTab !== 'Bundles') {
                if (!activetedTabs.includes('Bundles')) {
                    setActivetedTabs([...activetedTabs, 'Bundles']);
                }
                dispatch({
                    type: 'changeProductTab',
                    payload: 'Bundles',
                });
            }
        },
        callbackTab: (tab: EfProductNaverItemProps) => {
            if (tab.key !== activeTab) {
                if (!activetedTabs.includes(tab.key)) {
                    setActivetedTabs([...activetedTabs, tab.key]);
                }
                dispatch({
                    type: 'changeProductTab',
                    payload: tab.key,
                });
                window.sessionStorage.removeItem('toTop-scrollTop');
                window.scrollTo({
                    top: 0,
                    behavior: 'instant',
                });
            }
        },
    };

    const i18n = langText[locale] || langText.us;
    const [isClient, setIsClient] = useState(false);
    const [shoppingCarts, setShoppingCarts] = useState([]);
    const [visible, setVisible] = useState(false);

    const handleAddToCart = (card: any, list_name?: string) => {
        if (card?.inventory <= 0) {
            return;
        }
        const obj: ShopifyCartItemType = { ...card };
        const target = shoppingCarts.find((item: ShopifyCartItemType) => item?.productId === card?.productId && item?.variantId === card?.variantId);
        if (target) {
            obj.quantity = target.quantity + (target?.quantity < target.inventory ? 1 : 0);
            setShoppingCarts([
                obj,
                ...shoppingCarts?.filter((item: ShopifyCartItemType) => !(item?.productId === card?.productId && item?.variantId === card?.variantId)),
            ]);
        } else {
            obj.quantity = Number(obj?.inventory) > 0 ? 1 : 0;
            setShoppingCarts([obj, ...shoppingCarts]);
        }
        setVisible(true);
        gtm.push({
            event: 'uaEvent',
            event_name: 'bundle_add_to_cart',
            product_name: card?.name || card?.title,
            list_name: list_name || 'none',
            price: card?.discountPrice,
            tag: 'none',
            quantity: 1,
            button_name: i18n.cart,
        });
    };

    const handleCallback = (carts: ShopifyCartItemType[], isClose?: boolean) => {
        setShoppingCarts(carts);
        if (isClose) {
            setVisible(false);
        }
    };

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsClient(true);
        }
    });

    /** 检测浏览器是否支持使用 webp */
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const webP = new Image();
        webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
        webP.onload = () => {
            dispatch({
                type: 'changeCanWebp',
                payload: 1,
            });
        };
        webP.onerror = () => {
            dispatch({
                type: 'changeCanWebp',
                payload: -1,
            });
        };
    }, [dispatch]);

    return (
        <>
            <div className={`delta-3-ultra-series GtmModuleData-section ${css.section}`} style={{ minHeight: '100vh' }} data-section-id="DELTA 3 Ultra Series">
                <Naver {...naverProps} />
                {activetedTabs.includes('Product') && (
                    <div className={`page-tab tab-overview ${activeTab === 'Product' ? 'active' : ''}`}>
                        <Overview callbackCart={handleAddToCart} />
                    </div>
                )}
                {activetedTabs.includes('Bundles') && (
                    <div className={`page-tab tab-Bundle ${activeTab === 'Bundles' ? 'active' : ''}`}>
                        <Bundle callbackCart={handleAddToCart} />
                    </div>
                )}
                {activetedTabs.includes('Spec') && (
                    <div className={`page-tab tab-Spec ${activeTab === 'Spec' ? 'active' : ''}`}>
                        <Spec productSpecs={productSpecs} theme={theme} />
                    </div>
                )}
                {activetedTabs.includes('Support') && (
                    <div className={`page-tab tab-support ${activeTab === 'Support' ? 'active' : ''}`}>
                        <Support supportData={productSupport} theme={theme} />
                    </div>
                )}
                {visible && isClient && createPortal(<ShopifyShoppingCart callback={handleCallback} items={shoppingCarts} />, document.body)}
                {isClient &&
                    createPortal(
                        <ShopifyCartIcon
                            quantity={shoppingCarts.reduce((prev: number, curItem: ShopifyCartItemType) => {
                                return prev + Number(curItem.quantity);
                            }, 0)}
                            callback={() => setVisible(true)}
                        />,
                        document.body,
                    )}
            </div>
        </>
    );
};

export default DPUX;
